import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { lawyerService } from '../services/lawyerService';
import { entitySyncService } from '../services/entitySyncService';

// Fetches the main list of lawyers/firms
export const fetchLawyers = createAsyncThunk(
  'lawyers/fetchLawyers',
  async (filters = {}, { rejectWithValue }) => {
    try {
      // Correctly calls the service method
      const response = await lawyerService.fetchLawyers(filters);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const loadLawyersFromCache = createAsyncThunk(
  'lawyers/loadFromCache',
  async (_, { rejectWithValue }) => {
    try {
      const data = await entitySyncService.getLocalEntities('lawyers');
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetches a single lawyer/firm by their ID
export const fetchLawyerById = createAsyncThunk(
  'lawyers/fetchLawyerById',
  async (payload, { rejectWithValue }) => {
    const id = typeof payload === 'object' ? payload.id : payload;
    try {
      const response = await lawyerService.getLawyerById(id);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  },
  {
    condition: (payload, { getState }) => {
      if (payload?.forceRefresh) return true; // Always fetch if forced
      const { lawyers } = getState();
      if (lawyers.loading) return false;
      if (lawyers.selectedLawyer?.id === (payload?.id || payload)) return false;
    }
  }
);

// Creates a new lawyer/firm
export const createLawyer = createAsyncThunk(
  'lawyers/createLawyer',
  async (lawyerData, { rejectWithValue }) => {
    try {
      const response = await lawyerService.createLawyer(lawyerData);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Updates an existing lawyer/firm
export const updateLawyer = createAsyncThunk(
  'lawyers/updateLawyer',
  async ({ lawyerId, updates }, { rejectWithValue }) => {
    try {
      const response = await lawyerService.updateLawyer(lawyerId, updates);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Deletes a lawyer/firm
export const deleteLawyer = createAsyncThunk(
  'lawyers/deleteLawyer',
  async (lawyerId, { rejectWithValue }) => {
    try {
      await lawyerService.deleteLawyer(lawyerId);
      return lawyerId; // Return the ID for removal from the state
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const mergeLawyers = createAsyncThunk(
  'lawyers/mergeLawyers',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await lawyerService.mergeLawyers(payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Used by the "Add" modal to search for firms
export const searchLawyers = createAsyncThunk(
  'lawyers/searchLawyers',
  async (params, { rejectWithValue }) => {
    try {
      // This also uses the primary fetch method from the service
      const response = await lawyerService.fetchLawyers(params);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Specifically for the selection modal's contact list
export const fetchContactsForSelection = createAsyncThunk(
  'lawyers/fetchContactsForSelection',
  async (filters, { rejectWithValue }) => {
    try {
      // This thunk now correctly uses the main service fetch method
      const response = await lawyerService.fetchLawyers(filters);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const lawyerSlice = createSlice({
  name: 'lawyers',
  initialState: {
    lawyers: [],
    selectedLawyer: null,
    loading: false,
    error: null,
    totalPages: 1,
    totalLawyers: 0,
    filters: { page: 1, limit: 10, name: '', contactName: '' },

    // State for search results in the AddLawyerOrContactModal
    searchResults: [],
    searchLoading: false,
    searchError: null,
    searchPagination: null,

    // State for the LawyerSelectionModal
    contactList: [],
    contactListLoading: false,
    contactListError: null,
    contactPagination: null,

    // State for CUD (Create, Update, Delete) operations
    actionLoading: false,
    actionError: null,
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
      state.actionError = null;
      state.searchError = null;
      state.contactListError = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchPagination = null;
    },
    clearSelectedLawyer: (state) => {
      state.selectedLawyer = null;
    },
    clearContactList: (state) => {
      state.contactList = [];
      state.contactPagination = null;
      state.contactListError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Main list fetching
      .addCase(fetchLawyers.pending, (state) => { state.loading = true; })
      .addCase(fetchLawyers.fulfilled, (state, action) => {
        state.loading = false;
        state.lawyers = action.payload.data || [];
        state.totalPages = action.payload.pagination?.totalPages || 1;
        state.totalLawyers = action.payload.pagination?.totalItems || 0;
      })
      .addCase(fetchLawyers.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(loadLawyersFromCache.fulfilled, (state, action) => {
        state.lawyers = action.payload;
        state.totalLawyers = action.payload.length;
        state.totalPages = Math.ceil(action.payload.length / state.filters.limit);
      })

      // Fetch by ID
      .addCase(fetchLawyerById.pending, (state) => { state.loading = true; })
      .addCase(fetchLawyerById.fulfilled, (state, action) => { state.loading = false; state.selectedLawyer = action.payload; })
      .addCase(fetchLawyerById.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Modal search
      .addCase(searchLawyers.pending, (state) => { state.searchLoading = true; })
      .addCase(searchLawyers.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = action.payload.data || [];
        state.searchPagination = action.payload.pagination || null;
      })
      .addCase(searchLawyers.rejected, (state, action) => { state.searchLoading = false; state.searchError = action.payload; })

      // Contact list for selection modal
      .addCase(fetchContactsForSelection.pending, (state) => { state.contactListLoading = true; })
      .addCase(fetchContactsForSelection.fulfilled, (state, action) => {
        state.contactListLoading = false;
        const contacts = (action.payload.data || []).flatMap(firm =>
          (firm.contacts || []).map(contact => ({ ...contact, firm: { id: firm.id, name: firm.name } }))
        );
        state.contactList = contacts;
        state.contactPagination = action.payload.pagination || null;
      })
      .addCase(fetchContactsForSelection.rejected, (state, action) => { state.contactListLoading = false; state.contactListError = action.payload; })

      // Generic handlers for create, update, delete actions
      .addMatcher(
        (action) => [createLawyer.pending, updateLawyer.pending, deleteLawyer.pending, mergeLawyers.pending].includes(action.type),
        (state) => { state.actionLoading = true; state.actionError = null; }
      )
      .addMatcher(
        (action) => [createLawyer.fulfilled, updateLawyer.fulfilled, deleteLawyer.fulfilled, mergeLawyers.fulfilled].includes(action.type),
        (state, action) => {
          state.actionLoading = false;
          if (action.type === updateLawyer.fulfilled.type && state.selectedLawyer?.id === action.payload.id) {
            state.selectedLawyer = action.payload;
          }

          // Mirror to IndexedDB
          if (action.type === createLawyer.fulfilled.type || action.type === updateLawyer.fulfilled.type) {
            entitySyncService.upsertLocalEntity('lawyers', action.payload);
          } else if (action.type === deleteLawyer.fulfilled.type) {
            entitySyncService.deleteLocalEntity('lawyers', action.payload);
          }
        }
      )
      .addMatcher(
        (action) => [createLawyer.rejected, updateLawyer.rejected, deleteLawyer.rejected, mergeLawyers.rejected].includes(action.type),
        (state, action) => { state.actionLoading = false; state.actionError = action.payload; }
      );
  },
});

export const {
  setFilters,
  clearError,
  clearSearchResults,
  clearSelectedLawyer,
  clearContactList,
} = lawyerSlice.actions;

export default lawyerSlice.reducer;